/**
 * SVG construction helpers and the three Indian chart geometries
 *
 * ── Where these layouts come from ───────────────────────────────────────────
 *
 * ◆ All three are read off the standard Indian chart figure ("Indian Chart Styles"), which draws Lord Śrī Rāma's rāśi chart in all three formats from one
 * set of positions. That figure is a picture in the PDF, so it was rendered and
 * read rather than taken from memory — which matters, because the East Indian
 * corner assignments are not something to guess at, and a wrong one produces a
 * chart that looks entirely correct.
 *
 * ⚠ **The North Indian chart is bhāva-fixed; the South and East Indian charts
 * are rāśi-fixed.** The convention is: in North Indian format the box
 * holding "Asc" is always the same box and the *rāśi number* written in it
 * changes chart to chart; in South and East format the rāśis never move and the
 * lagna is marked wherever it falls. Rendering one with the other's logic gives
 * a chart that is plausible and wrong.
 *
 * ◆ Cell geometry is defined once, here, as plain polygon coordinates in a
 * 100 × 100 space. The components only place text into cells, so the three
 * renderers cannot drift apart about where bhāva 4 is.
 *
 * © Manish Jagdish Thatte
 */

export const SVG_NS = 'http://www.w3.org/2000/svg';

/** Create an SVG element with attributes and optional text. */
export function el(name, attrs = {}, text = null) {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined) continue;
    node.setAttribute(k, String(v));
  }
  if (text !== null) node.textContent = text;
  return node;
}

/** `M x y L x y … Z` from a list of [x, y]. */
export function polygonPath(points) {
  return points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join(' ') + ' Z';
}

/** The average of a polygon's vertices — good enough for label placement here. */
export function centroid(points) {
  const n = points.length;
  return [
    points.reduce((a, p) => a + p[0], 0) / n,
    points.reduce((a, p) => a + p[1], 0) / n
  ];
}

/* ──────────────────────────────────────────────────────────────────────────
 * North Indian — bhāva-fixed
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The square's both diagonals plus the rhombus of the four side midpoints, in a
 * 100 × 100 box. Twelve cells: four diamonds on the axes and eight triangles.
 *
 * Bhāva 1 is the **top-centre diamond** and the numbering runs
 * **anticlockwise** — 2 and 3 in the top-left, 4 at the left-centre, and so on.
 * Verified against Figure 1, where the top diamond carries "Asc / 1st" and the
 * left-centre diamond carries "4th" with Saturn, matching "Li – Saturn" in the
 * 4th from a Karka lagna.
 */
export const NORTH_CELLS = Object.freeze([
  /*  1 */ [[50, 0], [75, 25], [50, 50], [25, 25]],
  /*  2 */ [[0, 0], [50, 0], [25, 25]],
  /*  3 */ [[0, 0], [25, 25], [0, 50]],
  /*  4 */ [[0, 50], [25, 25], [50, 50], [25, 75]],
  /*  5 */ [[0, 100], [0, 50], [25, 75]],
  /*  6 */ [[0, 100], [25, 75], [50, 100]],
  /*  7 */ [[50, 100], [25, 75], [50, 50], [75, 75]],
  /*  8 */ [[100, 100], [50, 100], [75, 75]],
  /*  9 */ [[100, 100], [75, 75], [100, 50]],
  /* 10 */ [[100, 50], [75, 75], [50, 50], [75, 25]],
  /* 11 */ [[100, 0], [100, 50], [75, 25]],
  /* 12 */ [[100, 0], [75, 25], [50, 0]]
].map(Object.freeze));

/**
 * Where a cell's grahas are centred, and where its rāśi number sits.
 *
 * ⚠ **These are tuned per cell rather than derived, and the derivation is what
 * failed.** "Centroid, nudged toward the middle of the square" puts the anchor
 * of a corner triangle outside the triangle's own span at that height: for
 * bhāva 2 the nudged centroid is x = 30, while the triangle only runs from
 * x = 16.7 to x = 33.3 there, so a centred label spills past the edge. The bug
 * survived a bounding-box overflow test, because a triangle's bounding box is
 * twice its area.
 *
 * ◆ The North Indian geometry is fixed — it is the same twelve shapes in every
 * chart ever drawn — so twelve pairs of constants are not a shortcut, they are
 * the honest encoding of a fixed layout. Each label anchor is on the horizontal
 * mid-line of its cell's usable band; each number anchor is in the outer corner,
 * clear of the labels.
 *
 * ⚠ The two diamonds on the horizontal axis (bhāvas 4 and 10) are the tight
 * case: their number sits at the same height as their labels, so the label
 * anchor is pushed toward the centre of the square and `NORTH_LABEL_MAX_WIDTH`
 * reserves the corner.
 */
export const NORTH_LABEL_ANCHORS = Object.freeze([
  [50, 30],   //  1 top diamond, below its number
  [25, 15],   //  2 top-left triangle
  [12, 26],   //  3 left-upper triangle
  [30, 50],   //  4 left diamond, pushed right of its number
  [12, 74],   //  5 left-lower triangle
  [25, 85],   //  6 bottom-left triangle
  [50, 70],   //  7 bottom diamond, above its number
  [75, 85],   //  8 bottom-right triangle
  [88, 74],   //  9 right-lower triangle
  [70, 50],   // 10 right diamond, pushed left of its number
  [88, 26],   // 11 right-upper triangle
  [75, 15]    // 12 top-right triangle
].map(Object.freeze));

export const NORTH_NUMBER_ANCHORS = Object.freeze([
  [50, 7],  [9, 4],   [4, 12],  [7, 50],
  [4, 88],  [9, 96],  [50, 93], [91, 96],
  [96, 88], [93, 50], [96, 12], [91, 4]
].map(Object.freeze));

/** Extra width limits where the rāśi number shares the label's band. */
export const NORTH_LABEL_MAX_WIDTH = Object.freeze([
  40, 999, 999, 34, 999, 999, 40, 999, 999, 34, 999, 999
]);

/** The lines to stroke for a North Indian chart. */
export const NORTH_LINES = Object.freeze([
  [[0, 0], [100, 0]], [[100, 0], [100, 100]], [[100, 100], [0, 100]], [[0, 100], [0, 0]],
  [[0, 0], [100, 100]], [[100, 0], [0, 100]],
  [[50, 0], [100, 50]], [[100, 50], [50, 100]], [[50, 100], [0, 50]], [[0, 50], [50, 0]]
].map(Object.freeze));

/* ──────────────────────────────────────────────────────────────────────────
 * South Indian — rāśi-fixed
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * A 4 × 4 grid whose outer ring holds the twelve rāśis, clockwise, with Meṣa in
 * the top row's second cell. The inner 2 × 2 is one merged panel for the title.
 *
 * Verified against Figure 1: Pi Ar Ta Ge across the top, Cn Le down the right,
 * Vi Li Sc Sg along the bottom, Cp Aq up the left.
 */
const SOUTH_GRID = [
  [11, 0, 1, 2],
  [10, null, null, 3],
  [9, null, null, 4],
  [8, 7, 6, 5]
];

/** rāśi index → its cell rectangle `{ x, y, w, h }` in the 100 × 100 box. */
export const SOUTH_CELLS = Object.freeze((() => {
  const out = new Array(12);
  const s = 25;
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 4; col++) {
      const rashi = SOUTH_GRID[row][col];
      if (rashi === null) continue;
      out[rashi] = Object.freeze({ x: col * s, y: row * s, w: s, h: s, row, col });
    }
  }
  return out;
})());

/** The merged inner panel, for the chart's title block. */
export const SOUTH_CENTRE = Object.freeze({ x: 25, y: 25, w: 50, h: 50 });

/* ──────────────────────────────────────────────────────────────────────────
 * East Indian — rāśi-fixed, a third geometry again
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * A 3 × 3 grid in which each **corner** cell is split by a diagonal running from
 * the square's outer corner to that cell's inner corner, giving eight triangles;
 * the four edge-centre cells are whole; the middle cell is the title panel.
 * 8 + 4 = 12.
 *
 * The rāśis run **anticlockwise** from Meṣa at top centre — the opposite sense to
 * the South Indian chart, which is the detail that makes East Indian a third
 * geometry rather than a restyling of the second:
 *
 *     Ar  top centre            Cn  left centre
 *     Ta  top-left, top half    Le  bottom-left, left half
 *     Ge  top-left, left half   Vi  bottom-left, bottom half
 *     Li  bottom centre         Cp  right centre
 *     Sc  bottom-right, bottom  Aq  top-right, right half
 *     Sg  bottom-right, right   Pi  top-right, top half
 *
 * Verified cell by cell against Figure 1 using Rāma's positions: Sun in the top
 * centre (Ar), Saturn in the bottom centre (Li), Mars in the right centre (Cp),
 * lagna + Moon + Jupiter in the left centre (Cn), Rāhu in the right half of the
 * bottom-right corner (Sg), Venus in the top half of the top-right corner (Pi).
 *
 * ⚠ The East Indian style is drawn **without** an enclosing rectangle, with the corner diagonals
 * running off the edge, and notes "some people draw the east Indian format charts
 * with an enclosing rectangle". This renders the enclosed form, which prints and
 * scales better; the geometry inside is identical either way.
 */
export const EAST_CELLS = Object.freeze((() => {
  const t = 100 / 3;          // one grid step
  const out = new Array(12);

  // Edge-centre cells, whole.
  out[0] = [[t, 0], [2 * t, 0], [2 * t, t], [t, t]];                  // Ar  top
  out[3] = [[0, t], [t, t], [t, 2 * t], [0, 2 * t]];                  // Cn  left
  out[6] = [[t, 2 * t], [2 * t, 2 * t], [2 * t, 100], [t, 100]];      // Li  bottom
  out[9] = [[2 * t, t], [100, t], [100, 2 * t], [2 * t, 2 * t]];      // Cp  right

  // Top-left cell (0,0)–(t,t): diagonal from the outer corner [0,0] to the inner
  // corner [t,t]. Above it is Ta (touches the top edge); below it is Ge.
  out[1] = [[0, 0], [t, 0], [t, t]];
  out[2] = [[0, 0], [t, t], [0, t]];

  // Bottom-left cell: outer corner [0,100], inner corner [t, 2t].
  out[4] = [[0, 2 * t], [t, 2 * t], [0, 100]];                        // Le  left half
  out[5] = [[t, 2 * t], [t, 100], [0, 100]];                          // Vi  bottom half

  // Bottom-right cell: outer corner [100,100], inner corner [2t, 2t].
  out[7] = [[2 * t, 2 * t], [2 * t, 100], [100, 100]];                // Sc  bottom half
  out[8] = [[2 * t, 2 * t], [100, 100], [100, 2 * t]];                // Sg  right half

  // Top-right cell: outer corner [100,0], inner corner [2t, t].
  out[10] = [[100, t], [2 * t, t], [100, 0]];                         // Aq  right half
  out[11] = [[2 * t, t], [2 * t, 0], [100, 0]];                       // Pi  top half

  return out.map(Object.freeze);
})());

export const EAST_CENTRE = Object.freeze({ x: 100 / 3, y: 100 / 3, w: 100 / 3, h: 100 / 3 });

export const EAST_LINES = Object.freeze((() => {
  const t = 100 / 3;
  return [
    [[0, 0], [100, 0]], [[100, 0], [100, 100]], [[100, 100], [0, 100]], [[0, 100], [0, 0]],
    [[t, 0], [t, 100]], [[2 * t, 0], [2 * t, 100]],
    [[0, t], [100, t]], [[0, 2 * t], [100, 2 * t]],
    [[0, 0], [t, t]], [[100, 0], [2 * t, t]], [[0, 100], [t, 2 * t]], [[100, 100], [2 * t, 2 * t]]
  ].map(Object.freeze);
})());

/** Anchors for the East Indian cells, triangles nudged inward as in the North. */
export const EAST_ANCHORS = Object.freeze(EAST_CELLS.map((cell) => {
  const [cx, cy] = centroid(cell);
  if (cell.length === 4) return Object.freeze([cx, cy]);
  return Object.freeze([cx + (50 - cx) * 0.14, cy + (50 - cy) * 0.14]);
}));

/* ──────────────────────────────────────────────────────────────────────────
 * Fitting several grahas into one cell
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The horizontal extent of a polygon at a given y — its scanline width.
 *
 * ◆ This replaces "the width of the bounding box times a fudge factor", which
 * is badly wrong for the shapes a kundali is made of. A North Indian corner
 * triangle's bounding box is 50 × 25, but at the height where its label sits it
 * is only about 29 wide, and at the apex it is zero. Using the bounding box
 * over-estimates the room by nearly half and the text runs over the border.
 */
export function widthAtY(points, y) {
  const xs = [];
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    if (y1 === y2) continue;
    const lo = Math.min(y1, y2), hi = Math.max(y1, y2);
    if (y < lo || y > hi) continue;
    xs.push(x1 + ((y - y1) / (y2 - y1)) * (x2 - x1));
  }
  if (xs.length < 2) return 0;
  return Math.max(...xs) - Math.min(...xs);
}

/**
 * The width a line of text of this size can actually occupy at this height.
 *
 * ⚠ **`widthAtY` alone is not enough, and the gap is easy to miss.** A line of
 * text is a box, not a ray: it has an ascender and a descender. On a sloped edge
 * — every cell of a North Indian chart, and eight of twelve in an East Indian
 * one — a row can fit at its baseline and still push its upper corners through
 * the border. The first version of this checked only the baseline, reported that
 * a 1962 Makara stellium fitted, and drew two corners outside the cell.
 *
 * ◆ So the width is the narrowest of the band the text box actually covers.
 */
function usableWidth(cell, y, font) {
  const half = font * 0.42;
  return Math.min(
    widthAtY(cell, y - half),
    widthAtY(cell, y),
    widthAtY(cell, y + half)
  );
}

/**
 * ◆ **The layout problem nobody solves well: five grahas in one bhāva.** A
 * North Indian corner triangle has almost no room, and the usual outcomes are
 * text spilling over the border or a cell that silently shows only the first
 * three.
 *
 * The resolution order is decided **per cell**, never globally — a chart with a
 * stellium in one bhāva should not shrink the type in the other eleven. Within
 * a cell the search is font-major: at each size, from the largest down, every
 * row-count is tried before the size is reduced, so **wrapping is preferred to
 * shrinking**. Only when the floor size has been reached at every wrapping does
 * it fall back to the two-letter symbols, and only for that cell.
 *
 * ⚠ **The text is measured, not estimated, and the first version of this was
 * wrong because it estimated.** A character-width guess of 0.56 em is far too
 * generous for a serif face carrying Sanskrit diacritics — "Maṅgala Budha"
 * measured 37 units by that rule and rendered at well over 40, so three grahas
 * in the lagna bhāva bled across the chart's border. `getComputedTextLength()`
 * asks the browser, which is the only thing that actually knows.
 *
 * ⚠ Measuring means the `<g>` must already be **in the rendered document**.
 * Detached SVG returns 0 for every length, which silently reports that
 * everything fits. The chart components therefore append their `<svg>` before
 * filling the cells.
 *
 * `makeSpan(label, index, abbreviated)` builds one `<tspan>`, so the caller
 * keeps control of classes, tooltips and retrograde marks.
 *
 * Returns `{ fontSize, rows, abbreviated }` describing what was drawn.
 */
export function renderCellLabels(parent, labels, {
  cell, anchor, makeSpan, abbreviate, maxWidth = Infinity,
  baseFont = 5, minFont = 3.2, lineRatio = 1.2, sidePadding = 1.6
} = {}) {
  if (!labels.length) return { fontSize: baseFont, rows: [], abbreviated: false };

  const [ax, ay] = anchor;
  const attempts = [];
  for (const font of steps(baseFont, minFont, 0.2)) {
    for (let perRow = labels.length; perRow >= 1; perRow--) {
      attempts.push({ font, perRow, abbreviated: false });
    }
  }
  if (abbreviate) {
    for (const font of steps(baseFont, minFont, 0.2)) {
      for (let perRow = labels.length; perRow >= 1; perRow--) {
        attempts.push({ font, perRow, abbreviated: true });
      }
    }
  }

  // ◆ Rather than "try each option, then fall back to a fixed guess", every
  // attempt is scored by how far it overflows and the best is kept. A cell with
  // eight grahas in it — the February 1962 Makara conjunction is the real
  // case — has no layout that fits, and the useful behaviour there is the
  // *least bad* one, not an arbitrary two-column default.
  let drawn = null;
  let best = null;

  for (const attempt of attempts) {
    const source = attempt.abbreviated ? labels.map((l, i) => abbreviate(l, i)) : labels;
    const rows = chunk(source, attempt.perRow);
    const lineHeight = attempt.font * lineRatio;
    const top = ay - ((rows.length - 1) * lineHeight) / 2;

    const nodes = rows.map((row, r) => {
      const text = el('text', {
        x: ax, y: top + r * lineHeight,
        class: 'chart__grahas', 'text-anchor': 'middle',
        'dominant-baseline': 'middle', 'font-size': attempt.font
      });
      row.forEach((label, c) => {
        text.append(makeSpan(label, attempt.perRow * r + c, attempt.abbreviated));
        if (c < row.length - 1) text.append(document.createTextNode(' '));
      });
      return text;
    });
    for (const n of nodes) parent.append(n);

    // Every row must fit the polygon's width at its OWN y — a diamond is barely
    // any width near its points, so a tall block fails at the ends first.
    let overflow = 0;
    let measurable = false;
    nodes.forEach((node, r) => {
      const y = top + r * lineHeight;
      const available = Math.min(usableWidth(cell, y, attempt.font) - sidePadding * 2, maxWidth);
      const length = node.getComputedTextLength?.() ?? 0;
      // A detached node measures 0: treat that as "cannot verify" rather than
      // as "fits perfectly", or an off-document render silently reports success.
      if (length > 0) {
        measurable = true;
        overflow += Math.max(0, length - available);
      }
    });

    if (!measurable || overflow === 0) {
      drawn = { fontSize: attempt.font, rows, abbreviated: attempt.abbreviated, overflow: 0 };
      break;
    }

    if (!best || overflow < best.overflow) {
      best?.nodes.forEach((n) => n.remove());
      best = { nodes, overflow, result: { fontSize: attempt.font, rows, abbreviated: attempt.abbreviated, overflow } };
    } else {
      for (const n of nodes) n.remove();
    }
  }

  if (!drawn) {
    // ⚠ Nothing fitted. The least-bad layout is drawn rather than dropping a
    // graha: a chart quietly missing a planet is far worse than a cramped one,
    // and the planet table beneath always carries the full list. The marker
    // class lets the stylesheet tighten the tracking for these cells only.
    for (const n of best.nodes) n.classList.add('chart__grahas--dense');
    drawn = best.result;
  } else {
    best?.nodes.forEach((n) => n.remove());
  }

  return drawn;
}

function steps(from, to, by) {
  const out = [];
  for (let v = from; v >= to - 1e-9; v -= by) out.push(Number(v.toFixed(2)));
  return out;
}

function chunk(items, perRow) {
  const out = [];
  for (let i = 0; i < items.length; i += perRow) out.push(items.slice(i, i + perRow));
  return out;
}
